import React, { useEffect, useState, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Survey } from "survey-react-ui";
import { Model } from "survey-core";
import "survey-core/survey-core.css";
import "../../pages/quizSurvey/AdminDashboard.scss";
import dayjs from "dayjs";
import "./TakeSurvey.scss";
import api from "components/api/api";
import { Spinner } from "reactstrap";
import toast from "react-hot-toast";

function TakeSurveyPage() {
  const { id } = useParams();
  const history = useHistory();
  const [mainTitle, setMainTitle] = useState("");
  const [surveyModel, setSurveyModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);
  const [surveyData, setSurveyData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [immediateresultshow, setImmediateResultShow] = useState(false);
  const userId = localStorage.getItem("userId") || "anonymous";
  const startTimeKey = `survey-start-${id}`;

  const handleSeeResult = (id, type, title) => {
    localStorage.setItem("Title", title);
    history.replace(`/result/${id}/${type.toLowerCase()}`);
  };

  const fetchSurvey = async () => {
    try {
      const res = await api.fetchSurveyById(id, userId);

      if (!res.data.success) {
        toast.error("Failed to load the quiz or survey.");
        return;
      }
      const data = res.data.data;

      setSurveyData(data);
      setMainTitle(data.title);

      console.log("Fetched Survey Data:", data);

      const showImmediateResult = data.announcementMode === "IMMEDIATE";
      setImmediateResultShow(showImmediateResult);

      setSurveyModel((prevModel) => {
        if (prevModel) return prevModel;

        const model = new Model(data.definitionJson);
        model.surveyId = data.id;

        // Set bilingual titles for all questions
        model.getAllQuestions().forEach((question) => {
          const originalData = data.definitionJson.pages
            .flatMap((p) => p.elements)
            .find((e) => e.name === question.name);

          if (originalData) {
            question.jsonObj = originalData;

            if (originalData.arabicTitle) {
              question.title = `${originalData.title}\n${originalData.arabicTitle}`;
            } else {
              question.title = originalData.title;
            }
          }
        });

        // ✅ Force all questions to be required
        model.requiredText = "*";
        model.requiredErrorText = "This question is required.";
        model.getAllQuestions().forEach((question) => {
          question.isRequired = true;
        });

        const durationMinutes = parseInt(data.quizDuration) || 30;

        let storedStartTime = localStorage.getItem(startTimeKey);
        if (!storedStartTime) {
          storedStartTime = dayjs().toISOString();
          localStorage.setItem(startTimeKey, storedStartTime);
        }

        const endTime = dayjs(storedStartTime).add(durationMinutes, "minute");

        timerRef.current = setInterval(() => {
          const now = dayjs();
          const diff = endTime.diff(now, "second");

          if (diff <= 0) {
            clearInterval(timerRef.current);
            if (!model.isCompleted) {
              model.completeLastPage();
              model.doComplete();
            }
          } else {
            setTimeLeft(diff);
          }
        }, 1000);

        model.onAfterRenderQuestion.add((survey, options) => {
          if (options.question.getType() === "text") {
            const inputEl = options.htmlElement.querySelector("input");
            if (inputEl) {
              inputEl.addEventListener("input", () => {
                options.question.value = inputEl.value;
              });
            }
          }
          if (options.question.getType() === "dropdown") {
            const clearBtn = options.htmlElement.querySelector(
              ".sd-dropdown_clean-button",
            );
            if (clearBtn) {
              clearBtn.remove();
            }
          }
        });

        model.onComplete.add(async (sender) => {
          clearInterval(timerRef.current);
          const startTime = localStorage.getItem(startTimeKey);
          localStorage.removeItem(startTimeKey);
          const endTime = new Date();
          const finishTime = Math.floor((endTime - new Date(startTime)) / 1000);

          const payload = {
            userId,
            answers: sender.data,
            finishTime: finishTime,
          };

          try {
            setSubmitting(true);
            await api.submitSurvey(id, payload);
            if (data.type === "Survey") {
              toast.success("Survey submitted successfully!");
            } else {
              toast.success("Quiz submitted successfully!");
            }

            setTimeout(() => {
              if (data.visibilityType === "PRIVATE") {
                history.push("/quiz-survey");
              } else if (showImmediateResult) {
                console.log("Redirecting to result (IMMEDIATE mode)");
                handleSeeResult(data.id, data.type, data.title);
              } else {
                history.push("/quiz-survey");
              }
            }, 500);
          } catch (error) {
            console.error("Submission failed:", error);
            toast.error("Failed to submit quiz or survey.");
          } finally {
            setSubmitting(false);
          }
        });

        return model;
      });
    } catch (error) {
      console.error("Error fetching survey:", error);
      toast.error("An error occurred while loading the quiz or survey.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {}, [immediateresultshow]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue =
        "Are you sure you want to leave? Your progress will be lost.";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    const handlePopState = () => {
      const confirmLeave = window.confirm(
        "Going back will lose your progress. Continue?",
      );
      if (!confirmLeave) {
        history.push(0);
      }
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    fetchSurvey();
  }, [id]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  if (loading || submitting) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner
          animation="border"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        />
        {submitting && (
          <p className="ml-3">
            {surveyData.type === "Survey"
              ? "Submitting your survey..."
              : "Submitting your quiz..."}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {surveyModel ? (
        <div className="survey-panel">
          <div className="survey-header">
            <h2>{mainTitle || "hey"}</h2>
            <p className="timer">Time Left: {formatTime(timeLeft || 0)}</p>
          </div>
          <Survey model={surveyModel} />
          <button
            className="back-btn"
            onClick={() => {
              const confirmBack = window.confirm(
                "Your progress will be lost. Are you sure?",
              );
              if (confirmBack) history.push("/quiz-survey");
            }}
          >
            Back to Dashboard
          </button>
        </div>
      ) : (
        <p>Quiz or Survey not found or invalid.</p>
      )}
    </div>
  );
}

export default TakeSurveyPage;
